import React, { useState } from "react";
import { ShieldCheck, BadgeCheck, Users, PencilLine, ExternalLink, Info } from "lucide-react";
import { T } from "@/context/LanguageContext";

// Verification level → visual treatment + human label.
const LEVELS = {
  official: { dot: "bg-emerald-500", ring: "ring-emerald-500/30", text: "text-emerald-700", icon: ShieldCheck, label: "Official source" },
  verified: { dot: "bg-blue-500", ring: "ring-blue-500/30", text: "text-blue-700", icon: BadgeCheck, label: "Verified" },
  community: { dot: "bg-amber-500", ring: "ring-amber-500/30", text: "text-amber-700", icon: Users, label: "Community reported" },
  unverified: { dot: "bg-slate-400", ring: "ring-slate-400/30", text: "text-slate-600", icon: PencilLine, label: "Unverified draft" },
};

/**
 * Compact, expandable provenance badge shown under every governed fact.
 * Props: source {label, url, scope}, verification, year.
 */
export function SourceBadge({ source, verification = "verified", year }) {
  const [open, setOpen] = useState(false);
  const lvl = LEVELS[verification] || LEVELS.verified;
  const Icon = lvl.icon;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 rounded-full bg-white px-2 py-0.5 text-[10px] font-medium text-muted-foreground ring-1 ${lvl.ring} transition-colors hover:bg-parchment`}
        data-testid="source-badge"
      >
        <span className={`h-1.5 w-1.5 rounded-full ${lvl.dot}`} />
        {year && <span className="tabular-nums"><T>as of</T> {year}</span>}
        <Info className="h-3 w-3 opacity-60" />
      </button>
      {open && (
        <div
          className="absolute left-0 top-full z-30 mt-1.5 w-64 rounded-lg border border-border bg-white p-3 text-left shadow-xl"
          data-testid="source-badge-detail"
        >
          <div className={`mb-1.5 flex items-center gap-1.5 text-[11px] font-bold ${lvl.text}`}>
            <Icon className="h-3.5 w-3.5" /> <T>{lvl.label}</T>
          </div>
          <div className="text-xs font-semibold text-ink">{source?.label}</div>
          {source?.scope && (
            <div className="mt-0.5 text-[11px] text-muted-foreground">
              <T>Scope</T>: {source.scope}
            </div>
          )}
          {year && (
            <div className="text-[11px] text-muted-foreground"><T>Reference year</T>: {year}</div>
          )}
          {source?.url && (
            <a
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-oldglory-red hover:underline"
              data-testid="source-badge-link"
            >
              <T>View source</T> <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      )}
    </div>
  );
}

export default SourceBadge;
